/**
 * CapturedDecisionsPanel (FLOW-570, conflict check wired in FLOW-577)
 *
 * The review queue for decisions the AI pulled out of a committed breakdown
 * session: title, category, confidence, and a per-row Confirm / Dismiss.
 * Once a row is confirmed it's no longer "proposed" and drops out of this
 * queue; a dismissed row is gone entirely. Low-confidence rows are flagged
 * "Assumed" — same treatment as InitiativeSpecPanel's open decisions, so an
 * assumption never reads as something the user actually decided.
 *
 * Extraction is enrichment, not a gate: a failure here is a dismissible
 * banner, never a blocker for anything else on the page.
 *
 * FLOW-577: Confirm now runs FLOW-573's contradiction check against the
 * saved decision first, and warnings render inline via
 * `DecisionConflictWarnings` with `allowEdited={false}` (D5: this queue has
 * no edit form to return to). That flow lives in `useDecisionReview`
 * (FLOW-667), which `DecisionsPage` shares — this component supplies the
 * confirm/dismiss persistence and the markup, nothing else.
 */

import { Component, For, Show } from "solid-js";
import type {
  ConflictResolution,
  Decision,
  DecisionConflictWarning,
  ProjectId,
} from "../../api/types";
import { Badge, Spinner, type BadgeVariant } from "../ui";
import { DecisionConflictWarnings } from "../decisions/DecisionConflictWarnings";
import { useDecisionReview } from "../../hooks/useDecisionReview";
import { useCanConfirmDecisions } from "../../hooks/useCanConfirmDecisions";

export interface CapturedDecisionsPanelProps {
  decisions: Decision[];
  checked: boolean;
  extracting: boolean;
  error: string | null;
  projectId: ProjectId;
  /** Confirms the decision. Resolves to whether it succeeded. */
  onConfirm: (id: string) => Promise<boolean>;
  onDismiss: (id: string) => void;
  onClearError: () => void;
}

const capitalize = (value: string): string =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);

const CONFIDENCE_VARIANTS: Record<string, BadgeVariant> = {
  high: "high",
  medium: "medium",
  low: "low",
};

export const CapturedDecisionsPanel: Component<CapturedDecisionsPanelProps> = (
  props,
) => {
  const proposed = () =>
    props.decisions.filter((decision) => decision.status === "proposed");
  const canConfirm = useCanConfirmDecisions(() => props.projectId);
  const settled = () => props.checked && !props.extracting;

  const review = useDecisionReview(() => props.projectId, {
    onConfirm: (id) => props.onConfirm(id),
    onDismiss: (id) => props.onDismiss(id),
  });

  return (
    <section
      class="mt-8 rounded-md border border-line bg-surface p-6"
      aria-labelledby="captured-decisions-title"
    >
      <h2 id="captured-decisions-title" class="text-lg font-semibold text-ink">
        Decisions captured
      </h2>

      <Show when={props.error}>
        {(message) => (
          <div
            role="alert"
            class="mt-3 flex items-start justify-between gap-4 rounded border border-signal-red/25 bg-signal-red-tint px-4 py-3"
          >
            <p class="text-sm text-signal-red">{message()}</p>
            <button
              type="button"
              class="shrink-0 text-sm font-medium text-signal-red hover:text-ink"
              onClick={props.onClearError}
            >
              Dismiss
            </button>
          </div>
        )}
      </Show>

      <Show when={review.applyError()}>
        {(message) => (
          <div
            role="alert"
            class="mt-3 flex items-start justify-between gap-4 rounded border border-signal-red/25 bg-signal-red-tint px-4 py-3"
          >
            <p class="text-sm text-signal-red">{message()}</p>
            <button
              type="button"
              class="shrink-0 text-sm font-medium text-signal-red hover:text-ink"
              onClick={review.clearApplyError}
            >
              Dismiss
            </button>
          </div>
        )}
      </Show>

      <Show when={props.extracting}>
        <div
          role="status"
          class="mt-4 flex items-center gap-3 text-sm text-ink-3"
        >
          <Spinner size="sm" />
          Capturing decisions from this session…
        </div>
      </Show>

      <Show when={settled()}>
        <Show
          when={props.decisions.length > 0}
          fallback={
            <p class="mt-4 text-sm text-ink-3">
              No decisions captured from this session.
            </p>
          }
        >
          <Show
            when={proposed().length > 0}
            fallback={
              <p class="mt-4 text-sm text-ink-3">
                All captured decisions have been reviewed.
              </p>
            }
          >
            <ul class="mt-4 flex flex-col gap-3">
              <For each={proposed()}>
                {(decision) => (
                  <DecisionRow
                    decision={decision}
                    canConfirm={canConfirm()}
                    confirmDisabled={review.confirmDisabled(decision.id)}
                    onConfirmClick={() => review.confirm(decision.id)}
                    onCancelClick={review.cancel}
                    onDismissClick={() => review.dismiss(decision.id)}
                    showWarnings={review.showingWarnings(decision.id)}
                    warnings={review.warnings()}
                    choices={review.choices()}
                    onChoose={review.choose}
                    busy={review.applying()}
                  />
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </Show>
    </section>
  );
};

const DecisionRow: Component<{
  /** Mirrors the backend gate on confirmation — see `useCanConfirmDecisions` */
  canConfirm: boolean;
  decision: Decision;
  confirmDisabled: boolean;
  onConfirmClick: () => void;
  onCancelClick: () => void;
  onDismissClick: () => void;
  showWarnings: boolean;
  warnings: DecisionConflictWarning[];
  choices: Record<string, ConflictResolution | undefined>;
  onChoose: (warningId: string, outcome: ConflictResolution) => void;
  busy: boolean;
}> = (props) => (
  <li class="rounded-md border border-line p-4">
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <h3 class="text-sm font-semibold text-ink">{props.decision.title}</h3>
        <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Badge>{capitalize(props.decision.category)}</Badge>
          <Badge
            variant={
              CONFIDENCE_VARIANTS[props.decision.confidence] ?? "default"
            }
          >
            {capitalize(props.decision.confidence)} confidence
          </Badge>
          <Show when={props.decision.confidence === "low"}>
            <span class="inline-flex items-center rounded bg-signal-amber-tint px-1.5 py-0.5 text-xs font-medium text-signal-amber">
              Assumed
            </span>
          </Show>
        </div>
      </div>

      <div class="flex shrink-0 items-center gap-2">
        <Show when={props.showWarnings}>
          <button
            type="button"
            aria-label={`Cancel conflict review for "${props.decision.title}"`}
            disabled={props.busy}
            onClick={props.onCancelClick}
            class="rounded-full bg-sunken px-2.5 py-1 text-xs font-medium text-ink-2 hover:bg-line focus-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
        </Show>
        <button
          type="button"
          aria-label={`Confirm "${props.decision.title}"`}
          disabled={props.confirmDisabled}
          onClick={props.onConfirmClick}
          class="rounded-full bg-signal-green-tint px-2.5 py-1 text-xs font-medium text-signal-green hover:bg-signal-green/15 focus-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          Confirm
        </button>
        <button
          type="button"
          aria-label={`Dismiss "${props.decision.title}"`}
          onClick={props.onDismissClick}
          class="rounded-full bg-signal-red-tint px-2.5 py-1 text-xs font-medium text-signal-red hover:bg-signal-red/15 focus-ring"
        >
          Dismiss
        </button>
      </div>
    </div>

    <p class="mt-2 text-sm text-ink-2 whitespace-pre-wrap">
      {props.decision.rationale}
    </p>

    <Show when={props.showWarnings}>
      <div class="mt-3">
        <DecisionConflictWarnings
          warnings={props.warnings}
          choices={props.choices}
          onChoose={props.onChoose}
          allowEdited={false}
          busy={props.busy}
        />
      </div>
    </Show>
  </li>
);

export default CapturedDecisionsPanel;
